import type { IdentityOktaRequestsQuery, OktaSyncQuery } from "src/graphql/generated";
import { titleFor } from "src/lib/audit";

export type OktaSync = OktaSyncQuery["oktaSync"][number];
export type OktaRequest = IdentityOktaRequestsQuery["identityOktaRequests"][number];

const OPERATION_LABELS: Record<string, string> = {
  "user.create": "Created in Okta",
  "user.update": "Updated in Okta",
  "user.delete": "Removed from Okta",
  "session.bulk_upsert": "Sent in a bulk export"
};

// The two reasons that are not an audit action: an export somebody asked for, which
// changes nothing here and so writes no audit entry to take a title from.
const EXPORT_REASONS: Record<string, string> = {
  "okta.export_identity": "Export to Okta",
  "okta.bulk_export": "Bulk export"
};

/**
 * What one request did to the person in Okta, and what caused it, in the words the
 * history panel above uses for the same action.
 */
export function describeOktaRequest(request: OktaRequest): { what: string; why: string } {
  return {
    what: OPERATION_LABELS[request.operation] ?? request.operation,
    why: EXPORT_REASONS[request.reason] ?? titleFor(request.reason)
  };
}

type Tone = "slate" | "green" | "amber" | "red";

/**
 * What the Okta sync cell and a person's page show. No tone means plain text rather
 * than a badge: the person has no current term and was never sent, so there is
 * nothing for Okta to hold. `detail` is why the latest request failed.
 */
export type OktaSyncSummary = { label: string; tone?: Tone; caption?: string; detail?: string };

/**
 * Okta should hold everyone with a current term, as they are now, and nobody else.
 * The state says whether it does; the caption says since when, or what is behind.
 *
 * In every state but FAILED and the never-sent ones, the latest request is also the
 * last one Okta accepted, so its operation says whether that was a removal.
 */
export function describeOktaSync(sync: OktaSync): OktaSyncSummary {
  const last = sync.lastRequest;
  switch (sync.state) {
    case "IN_SYNC":
      if (last?.operation === "user.delete") {
        return { label: "Removed", tone: "slate", caption: since("Removed", sync.syncedAt) };
      }
      // Not "In sync": Okta accepting a request does not show it kept every value in it.
      return { label: "Success", tone: "green", caption: since("Accepted", sync.syncedAt) };
    case "OUT_OF_DATE":
      return { label: "Out of date", tone: "amber", caption: behind(sync) };
    case "FAILED":
      return {
        label: "Sync failed",
        tone: "red",
        caption: last ? since("Failed", last.sentAt) : undefined,
        detail: last?.error ?? undefined
      };
    case "NEVER_SENT":
      return { label: "Never sent", tone: "amber", caption: "Not yet in Okta" };
    case "NOT_IN_OKTA":
      return { label: "Not in Okta", caption: "No current term" };
  }
}

/** A moment the sync status names, to the minute, in the reader's own time zone. */
export function formatSyncTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function since(verb: string, iso: string | null): string | undefined {
  return iso ? `${verb} ${formatSyncTime(iso)}` : undefined;
}

function behind(sync: OktaSync): string {
  if (sync.lastRequest?.operation === "user.delete") {
    return "Removed, but holds a current term";
  }
  if (sync.changedAt && sync.syncedAt && Date.parse(sync.changedAt) > Date.parse(sync.syncedAt)) {
    return "Edited since the last sync";
  }
  return "No current term, still in Okta";
}
